// ProposalCard — one proposed change (standing-agent-ui "Proposal review
// surface", "Per-proposal decisions", "Role-aware controls"; tasks 9.1, 9.2,
// 9.5). Plain title, target chip, tool name in mono, risk label, the agent's
// rationale, readable arguments, "proposed N times", status. A pending card
// offers Approve / Deny (optional reason, "it will remember") / Edit (schema
// form + before/after diff → "Approve with changes"); a decision can be undone
// until the approved actions start. Controls a Member can't use stay visible,
// disabled, with the reason shown — never hidden.

import { forwardRef, useMemo, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from '@progress/kendo-react-inputs';
import { SvgIcon } from '@progress/kendo-react-common';
import { warningTriangleIcon, infoCircleIcon } from '@progress/kendo-svg-icons';
import type { Proposal } from '@shared/api-types';
import { formatDateTime } from '@shared/datetime';
import { ArgsView } from '../../../components/ArgsView';
import { JsonTree } from '../../../components/JsonTree';
import { ProposalStatusBadge } from '../components/StatusBadges';
import { APPROVER_ONLY_REASON } from '../pulsarUi';
import { ArgsDiff } from './ArgsDiff';
import { SchemaArgsForm } from './SchemaArgsForm';
import { cleanArgs, diffArgs, schemaFields, validateArgs } from './schemaArgs';

export type CardPanel = 'deny' | 'edit' | null;

export interface ProposalCardProps {
  proposal: Proposal;
  /** 1-based position, used in announcements ("Proposal 2 done"). */
  index: number;
  agentName: string;
  current: boolean;
  canApprove: boolean;
  panel: CardPanel;
  onPanel: (panel: CardPanel) => void;
  onFocusCard: () => void;
  onApprove: () => void;
  onDeny: (reason: string | null) => void;
  onEdit: (args: Record<string, unknown>) => void;
  onUndo: () => void;
  /** Decisions can be undone until the approved actions start. */
  undoable: boolean;
  busy: boolean;
}

const RISK_LABEL: Record<Proposal['risk'], string> = {
  low: 'Low risk',
  medium: 'Medium risk',
  high: 'High risk',
  unknown: 'Risk not stated',
};

function DenyForm({ agentName, onDeny, onCancel, busy }: { agentName: string; onDeny: (r: string | null) => void; onCancel: () => void; busy: boolean }) {
  const [reason, setReason] = useState('');
  return (
    <form
      className="pz-deny-form"
      onSubmit={(e) => {
        e.preventDefault();
        onDeny(reason.trim() || null);
      }}
    >
      <label className="pz-field-label" htmlFor="pz-deny-reason">
        Reason (optional)
      </label>
      <Input
        id="pz-deny-reason"
        value={reason}
        autoFocus
        maxLength={500}
        onChange={(e) => setReason(String(e.value ?? ''))}
        aria-describedby="pz-deny-hint"
        placeholder="never reboot servers during business hours"
      />
      <p id="pz-deny-hint" className="pz-hint">
        Tell {agentName} why — it will remember.
      </p>
      <div className="pz-inline">
        <Button type="submit" disabled={busy}>
          Deny
        </Button>
        <Button type="button" fillMode="flat" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function EditForm({
  proposal,
  canApprove,
  onSubmit,
  onCancel,
  busy,
}: {
  proposal: Proposal;
  canApprove: boolean;
  onSubmit: (args: Record<string, unknown>) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const original = useMemo(() => proposal.editedArgs ?? proposal.args, [proposal]);
  const fields = useMemo(() => schemaFields(proposal.toolInputSchema, original), [proposal.toolInputSchema, original]);
  const [draft, setDraft] = useState<Record<string, unknown>>(original);
  const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({});
  const errors = validateArgs(fields, draft);
  const changes = diffArgs(original, cleanArgs(fields, draft));
  const blocked = Object.keys(errors).length > 0 || Object.keys(jsonErrors).length > 0;

  return (
    <form
      className="pz-deny-form"
      aria-label={`Edit ${proposal.title}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!blocked && changes.length > 0 && canApprove) onSubmit(cleanArgs(fields, draft));
      }}
    >
      <SchemaArgsForm
        idPrefix={`pz-edit-${proposal.id}`}
        fields={fields}
        value={draft}
        onChange={setDraft}
        errors={errors}
        onJsonError={(name, msg) =>
          setJsonErrors((cur) => {
            const next = { ...cur };
            if (msg) next[name] = msg;
            else delete next[name];
            return next;
          })
        }
      />
      <div>
        <span className="pz-field-label">Before and after</span>
        <ArgsDiff changes={changes} />
      </div>
      <div className="pz-inline">
        <Button type="submit" disabled={busy || blocked || changes.length === 0 || !canApprove}>
          Approve with changes
        </Button>
        <Button type="button" fillMode="flat" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function resultText(result: unknown): string | null {
  if (result == null) return null;
  if (typeof result === 'string') return result;
  if (typeof result === 'object' && 'summary' in (result as Record<string, unknown>)) {
    const s = (result as Record<string, unknown>).summary;
    if (typeof s === 'string') return s;
  }
  return null;
}

export const ProposalCard = forwardRef<HTMLElement, ProposalCardProps>(function ProposalCard(props, ref) {
  const { proposal: p, canApprove, panel, busy } = props;
  const pending = p.status === 'pending';
  const titleId = `pz-card-${p.id}-title`;
  const reasonId = `pz-card-${p.id}-approver-reason`;
  const args = p.editedArgs ?? p.args;
  const changed = useMemo(
    () => (p.editedArgs ? new Set(diffArgs(p.args, p.editedArgs).map((c) => c.name)) : undefined),
    [p.args, p.editedArgs],
  );
  const result = resultText(p.result);

  return (
    <article
      ref={ref}
      className={`pz-card${props.current ? ' current' : ''}`}
      aria-labelledby={titleId}
      tabIndex={-1}
      onFocus={props.onFocusCard}
      data-proposal-id={p.id}
    >
      <div className="pz-card-head">
        <h3 className="pz-card-title" id={titleId}>
          {p.title}
        </h3>
        <ProposalStatusBadge status={p.status} />
        {p.target?.label && (
          <span className="pz-chip" title={p.target.kind ?? undefined}>
            {p.target.kind && <span className="pz-muted">{p.target.kind}:</span>} {p.target.label}
          </span>
        )}
        <span className="pz-mono">{p.tool}</span>
        <span className="pz-risk" data-risk={p.risk}>
          <SvgIcon icon={p.risk === 'high' ? warningTriangleIcon : infoCircleIcon} size="xsmall" aria-hidden />
          {RISK_LABEL[p.risk] ?? p.risk}
        </span>
        {p.timesProposed > 1 && <span className="pz-muted">proposed {p.timesProposed} times</span>}
      </div>

      <div className="pz-card-body">
        <div>
          <span className="pz-field-label">Why</span>
          <p style={{ margin: 0 }}>{p.rationale || <span className="pz-muted">No reason given.</span>}</p>
          {(p.alwaysAsks || p.needsIndividualReview) && pending && (
            <p className="pz-hint">This one needs its own review — it is never approved in bulk.</p>
          )}
        </div>
        <div>
          <span className="pz-field-label">What it will send</span>
          <ArgsView args={args} label={`Arguments for ${p.tool}`} changed={changed} />
        </div>
      </div>

      {/* Outcome lines */}
      {p.status === 'denied' && (
        <p className="pz-hint">
          Denied{p.decidedByName ? ` by ${p.decidedByName}` : ''}{p.decidedVia === 'teams' ? ' in Teams' : ''}
          {p.decidedUtc ? ` · ${formatDateTime(p.decidedUtc)}` : ''}
          {p.reason ? `: “${p.reason}”` : '.'}
          {p.reason && ` Saved to what ${props.agentName} has learned.`}
        </p>
      )}
      {(p.status === 'approved' || p.status === 'autoApproved') && (
        <p className="pz-hint">
          {p.status === 'autoApproved'
            ? `Approved automatically${p.grantedByName ? ` (allowed by ${p.grantedByName})` : ''}.`
            : `Approved${p.decidedByName ? ` by ${p.decidedByName}` : ''}${p.decidedVia === 'teams' ? ' in Teams' : ''}` +
              `${p.decidedUtc ? ` · ${formatDateTime(p.decidedUtc)}` : ''}.`}
          {p.decision === 'edit' && ' With changes.'}
        </p>
      )}
      {p.status === 'expired' && <p className="pz-hint">Expired {formatDateTime(p.expiresUtc)} without a decision.</p>}
      {p.status === 'done' && (
        <div className="pz-hint">
          Done{p.executedUtc ? ` ${formatDateTime(p.executedUtc)}` : ''}.{' '}
          {result ? result : p.result != null ? <JsonTree value={p.result} /> : null}
        </div>
      )}
      {p.status === 'failed' && (
        <p className="pz-error">Failed: {p.error || 'the action did not complete.'}</p>
      )}

      {pending && panel === null && (
        <>
          <div className="pz-card-actions">
            <Button
              type="button"
              aria-disabled={!canApprove || busy ? true : undefined}
              aria-describedby={!canApprove ? reasonId : undefined}
              title={!canApprove ? APPROVER_ONLY_REASON : undefined}
              onClick={() => canApprove && !busy && props.onApprove()}
            >
              Approve
            </Button>
            <Button type="button" disabled={busy} onClick={() => props.onPanel('deny')}>
              Deny
            </Button>
            <Button
              type="button"
              fillMode="flat"
              aria-disabled={!canApprove || busy ? true : undefined}
              aria-describedby={!canApprove ? reasonId : undefined}
              title={!canApprove ? APPROVER_ONLY_REASON : undefined}
              onClick={() => canApprove && !busy && props.onPanel('edit')}
            >
              Edit
            </Button>
          </div>
          {!canApprove && (
            <p id={reasonId} className="pz-hint">
              {APPROVER_ONLY_REASON}. You can still deny with a reason.
            </p>
          )}
        </>
      )}
      {pending && panel === 'deny' && (
        <DenyForm agentName={props.agentName} onDeny={props.onDeny} onCancel={() => props.onPanel(null)} busy={busy} />
      )}
      {pending && panel === 'edit' && (
        <EditForm proposal={p} canApprove={canApprove} onSubmit={props.onEdit} onCancel={() => props.onPanel(null)} busy={busy} />
      )}
      {props.undoable && ((p.status === 'approved' && canApprove) || p.status === 'denied') && (
        <div className="pz-card-actions">
          <Button type="button" fillMode="flat" disabled={busy} onClick={props.onUndo}>
            Undo
          </Button>
        </div>
      )}
    </article>
  );
});

export default ProposalCard;
